import {assertDataPresent} from '@github-ui/assert-data-present'
import {MergeBoxNudgeSection} from '@github-ui/in-product-targeting/merge-box-nudge-section/MergeBoxNudgeSection'
import {useFeatureStates} from '@github-ui/pull-requests/use-feature-state'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {GitMergeIcon, GitMergeQueueIcon} from '@primer/octicons-react'
import {Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {lazy, memo, Suspense, useCallback, useMemo, useRef, useState} from 'react'
import {useIsMergeBoxSidePanel} from '../contexts/MergeBoxLayoutContext'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {MergeBoxStatusCalculator} from '../helpers/merge-box-status-calculator/merge-box-status-calculator'
import {mergeBoxStatusPresentation} from '../helpers/merge-box-status-presentation'
import {presentationForStatus} from '../helpers/mergeability-status'
import {shouldShowAgentMerge} from '../helpers/should-show-agent-merge'
import {useMergeabilityLiveUpdates, type MergeabilityLiveUpdateChannels} from '../hooks/use-mergeability-live-updates'
import {useStackRebaseStatus} from '../hooks/mutations/use-stack-rebase-mutation'
import {useRebaseStack} from '../hooks/use-rebase-stack'
import {useMergeBoxPageData, useMergeBoxPageDataQueryKey} from '../page-data/loaders/use-merge-box-page-data'
import {useStackEntryMergeabilityQueries} from '../page-data/loaders/use-stack-mergeability-page-data'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {JSONAPIPullRequestPayload, PullRequestMergeRequirementsPayload} from '../page-data/payloads/merge-box'
import {MergeAction, type ProofOfPresenceRequirement, type PullRequestMergeAction} from '../types'
import {ClosedOrMergedStateMergeBox} from './ClosedOrMergedStateMergeBox'
import {MergeabilityIcon} from './MergeabilityIcon'
import styles from './MergeBox.module.css'
import {MergeBoxErrorState} from './MergeBoxErrorState'
import {BlockedSection} from './sections/BlockedSection'
import {ChecksSection} from './sections/ChecksSection'
import {ChecksSectionFetchFailure} from './sections/ChecksSectionFetchFailure'
import {ConflictsSection} from './sections/ConflictsSection'
import {DraftStateSection} from './sections/DraftStateSection'
import {MergeQueueSection} from './sections/merge-queue-section/MergeQueueSection'
import {MergeSection} from './sections/merge-section/MergeSection'
import {ReviewerSection} from './sections/ReviewerSection'
import {ProofOfPresenceSection} from './sections/ProofOfPresenceSection'
import {ConvertToDraftSection} from './sections/ConvertToDraftSection'
import {ClosePullRequestSection} from './sections/ClosePullRequestSection'
import {StackSection} from './sections/StackSection'
import {StacksMergeConflictsAndRebaseSection} from './sections/StacksMergeConflictsAndRebaseSection'
import {RebasingStacksSection} from './sections/rebasing-stacks-section/RebasingStacksSection'

// Lazy-load the feature-gated Agent Merge section so its ControlGroup,
// ButtonWithDropdown, and data hooks stay out of the merge-box bundle for
// users outside the rollout.
const AgentMergeSection = lazy(async () => {
  const module = await import('./sections/AgentMergeSection')
  return {default: module.AgentMergeSection}
})

function getProofOfPresenceRequirement(
  mergeRequirements: PullRequestMergeRequirementsPayload | null,
  pullRequestPath: string,
): ProofOfPresenceRequirement | null {
  const rulesCondition = mergeRequirements?.conditions.find(condition => condition.type === 'PULL_REQUEST_RULES')
  const pullRequestRollup = rulesCondition?.ruleRollups.find(ruleRollup => ruleRollup.ruleType === 'PULL_REQUEST')
  const metadata = pullRequestRollup?.metadata
  if (metadata && 'proofOfPresence' in metadata && metadata.proofOfPresence) {
    return {
      pullRequestPath,
      message: 'An authorized user must authenticate to merge.',
      metadata: metadata.proofOfPresence,
    }
  }

  return null
}

function MergeBoxLoading() {
  const hideBorder = useIsMergeBoxSidePanel()

  return (
    <div className={clsx(styles.mergeboxLoading, hideBorder && styles.hideBorder)}>
      <Spinner />
    </div>
  )
}

type MergeBoxWithSuspenseProps = {
  /**
   * When true, hides the mergeability icon that is shown to the left of the mergebox
   */
  hideIcon?: boolean
  /**
   * The current user's display login
   */
  viewerLogin: string
  /**
   *  The base help docs URL for the environment
   */
  helpUrl: string
  /**
   * The alive channels used for refetching mergeability updates
   */
  channels: MergeabilityLiveUpdateChannels
}
/**
 * Provides a suspense boundary for the mergebox
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the component name
export const MergeBoxWithSuspense = memo(function MergeBoxWithSuspense({
  hideIcon = false,
  ...rest
}: MergeBoxWithSuspenseProps) {
  return (
    <ErrorBoundary boundaryName="MergeBox" critical fallback={<MergeBoxErrorState hideIcon={hideIcon} />}>
      <Suspense>
        <MergeBoxWrapper hideIcon={hideIcon} {...rest} />
      </Suspense>
    </ErrorBoundary>
  )
})

/**
 * Loads the data for the MergeBox and translates it into an agnostic format for the MergeBox
 */
export function MergeBoxWrapper({
  viewerLogin,
  channels,
  ...rest
}: {
  hideIcon?: boolean
  viewerLogin: string
  helpUrl: string
  channels: MergeabilityLiveUpdateChannels
}) {
  const {mergeMethod} = useMergeMethodContext()

  const {data, dataUpdatedAt, isLoading, error} = useMergeBoxPageData({
    mergeMethod,
    bypassRequirements: false,
  })
  const queryKey = useMergeBoxPageDataQueryKey()

  const aliveChannels = useMemo(() => {
    if (data?.pullRequest.mergeBoxAliveChannels) return data?.pullRequest.mergeBoxAliveChannels

    return channels
  }, [channels, data])

  useMergeabilityLiveUpdates({queryKey, channels: aliveChannels})

  if (isLoading) return <MergeBoxLoading />
  // re-throw error to bubble up to our ErrorBoundary wrapping this component.
  if (error) throw error
  if (!data) return null

  return (
    // This suspense boundary ensures the entire merge box shows a loading state if any children of the merge box are fetching/consuming different data via `useSuspenseQuery` calls that are not wrapped in their own `Suspense` boundary.
    // Specifically, we don't render the merge box until we've received both core merge box data and status checks data.
    <Suspense fallback={<MergeBoxLoading />}>
      <h2 className="sr-only">Merge info</h2>
      <MergeBox
        mergeBoxDataUpdatedAt={dataUpdatedAt}
        mergeRequirements={data.mergeRequirements}
        pullRequest={data.pullRequest}
        pullRequestAliveChannel={aliveChannels?.pullRequestChannel}
        viewerLogin={viewerLogin}
        {...rest}
      />
    </Suspense>
  )
}

export type MergeBoxProps = {
  helpUrl: string
  hideIcon?: boolean
  mergeBoxDataUpdatedAt: number
  mergeRequirements: PullRequestMergeRequirementsPayload | null
  pullRequest: JSONAPIPullRequestPayload
  /** Alive channel carrying pull request events, forwarded to `AgentMergeSection`. */
  pullRequestAliveChannel?: string | null
  viewerLogin: string
}
/**
 * The actual mergebox with sections
 */
export function MergeBox({
  helpUrl,
  hideIcon = false,
  mergeBoxDataUpdatedAt,
  mergeRequirements,
  pullRequest: pullRequestData,
  pullRequestAliveChannel,
  viewerLogin,
}: MergeBoxProps) {
  const isSidePanel = useIsMergeBoxSidePanel()
  const hideBorder = isSidePanel
  const directMergeAction = pullRequestData.viewerMergeActions.find(action => action.name === MergeAction.DIRECT_MERGE)
  assertDataPresent(directMergeAction)
  const [isConfirmingMergeInfo, setIsConfirmingMergeInfo] = useState(false)
  const [pendingStackMergeAction, setPendingStackMergeAction] = useState<PullRequestMergeAction | undefined>()

  // The direct merge mutation polls from inside ConfirmMerge, so it has to stay mounted until
  // the merge settles. MergeSection hides it instead, and the remaining sections render alongside.
  const isPendingDirectMerge = pendingStackMergeAction === MergeAction.DIRECT_MERGE
  const shouldRenderAllSections = !isConfirmingMergeInfo || isPendingDirectMerge

  // When true, `RebasingStacksSection` takes over the merge box.
  // It only shows the rebasing statuses and only lets the user leave (via a "Done" CTA)
  // once the rebase has fully settled (succeeded, failed or mix of both).
  const [isRebasingStack, setIsRebasingStack] = useState(false)

  const [shouldFocusPrimaryMergeButton, setShouldFocusPrimaryMergeButton] = useState(false)
  const {isInMergeQueue, state, viewerCanClose, viewerCanDeleteHeadRef, viewerCanRestoreHeadRef} = pullRequestData
  const showConvertToDraft = pullRequestData.viewerCanConvertToDraft && !isInMergeQueue
  const showClosePullRequest = isSidePanel && viewerCanClose && !isInMergeQueue && state === 'OPEN'
  const shouldHideIcon =
    hideIcon || ((state === 'CLOSED' || state === 'MERGED') && !viewerCanDeleteHeadRef && !viewerCanRestoreHeadRef)

  // Intentionally the non-suspense, non-throwing variant: the checks endpoint
  // refetches independently for live updates, and a failure here must stay
  // scoped to `ChecksSection`'s error boundary rather than blow up the whole
  // MergeBox. See `useStatusChecksPageDataWithoutError` for the full rationale.
  const statusChecksData = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: pullRequestData.headRefOid,
  }).data

  const {mergeMethod} = useMergeMethodContext()
  const stackMergeabilityQueries = useStackEntryMergeabilityQueries({
    mergeRequirements,
    currentPullRequestId: pullRequestData.id,
    mergeMethod,
    bypassRequirements: false,
  })

  const featureStates = useFeatureStates()
  const {isRebasing} = useStackRebaseStatus()

  // Merge Box Status Calculator Helpers
  const statusCalculator = new MergeBoxStatusCalculator(
    pullRequestData,
    mergeRequirements,
    statusChecksData,
    featureStates,
    stackMergeabilityQueries,
    isRebasing,
    pendingStackMergeAction,
    mergeBoxDataUpdatedAt,
  )
  const mergeBoxRollupStatus = statusCalculator.overallStatus
  const blockedSectionStatus = statusCalculator.sections.BlockedSection
  const checksSectionStatus = statusCalculator.sections.ChecksSection
  const closedOrMergedStateSectionStatus = statusCalculator.sections.ClosedOrMergedStateMergeBox
  const conflictsSectionStatus = statusCalculator.sections.ConflictsSection
  const draftStateSectionStatus = statusCalculator.sections.DraftStateSection
  const mergeQueueSectionStatus = statusCalculator.sections.MergeQueueSection
  const mergeSectionStatus = statusCalculator.sections.MergeSection
  const reviewerSectionStatus = statusCalculator.sections.ReviewerSection
  const stackSectionStatus = statusCalculator.sections.StackSection
  const stacksMergeConflictsAndRebaseSectionStatus = statusCalculator.sections.StacksMergeConflictsAndRebaseSection

  // Determines the text for the icon
  const mergeStatusPresentation = presentationForStatus(mergeSectionStatus.mergeabilityStatus)
  const mergeBoxOverallStatusPresentation = mergeBoxStatusPresentation(mergeBoxRollupStatus, !shouldRenderAllSections)

  const userStateCondition = mergeRequirements?.conditions.find(
    condition => condition.type === 'PULL_REQUEST_USER_STATE',
  )
  const repositoryStateCondition = mergeRequirements?.conditions.find(
    condition => condition.type === 'PULL_REQUEST_REPO_STATE',
  )

  const {canUserPushToBase, isUserBlockedFromPushing} = mergeSectionStatus
  const showAgentMerge = shouldShowAgentMerge({
    isOpen: state === 'OPEN',
    isFeatureEnabled: featureStates.agentMerge === 'ENABLED',
    viewerCanUseCopilotCodingAgent: pullRequestData.viewerCanUseCopilotCodingAgent,
    repositoryStateCondition,
    userStateCondition,
    isUserBlockedFromPushing,
    isConfirmingMergeInfo,
  })
  const proofOfPresenceRequirement = getProofOfPresenceRequirement(
    mergeRequirements,
    new URL(pullRequestData.resourcePath, 'https://github.com').pathname,
  )
  const mergeBoxIcon = isInMergeQueue ? GitMergeQueueIcon : GitMergeIcon

  // Eligibility for the StacksMergeConflictsAndRebaseSection rebase CTA: the viewer must be
  // able to push to the base branch, not be blocked by an authorization policy, and the
  // section must be rendering more than just the stack list; relevance is otherwise decided
  // by the section status.
  const canRebaseStack = canUserPushToBase && !isUserBlockedFromPushing && !stackSectionStatus.shouldOnlyRenderStackList

  const shouldHideMergeSection = !mergeSectionStatus.shouldRender
  const isStackMergeInProgress = stackSectionStatus.isStackMergeInProgress
  const mergeBoxContainerRef = useRef<HTMLDivElement>(null)

  const handleStackMergePendingChange = (pendingAction?: PullRequestMergeAction) => {
    // The merge flow hides the whole merge section while pending, taking the focused
    // button with it.
    mergeBoxContainerRef.current?.focus()
    setPendingStackMergeAction(pendingAction)
  }

  const focusPrimaryMergeButton = useCallback(() => {
    if (shouldHideMergeSection || isStackMergeInProgress) {
      mergeBoxContainerRef.current?.focus()
    } else {
      setShouldFocusPrimaryMergeButton(true)
    }
  }, [isStackMergeInProgress, shouldHideMergeSection])

  // When a stack merge starts, the MergeSection (and ConfirmMerge) unmounts before
  // the mutation's onSuccess can reset isConfirmingMergeInfo. Reset it here so
  // shouldRenderAllSections becomes true and StackSection stays visible.
  if (isStackMergeInProgress && isConfirmingMergeInfo && !isPendingDirectMerge) {
    setIsConfirmingMergeInfo(false)
  }

  const mergeBoxUserPreferences = pullRequestData.mergeBoxUserPreferences
  const borderColor = mergeBoxOverallStatusPresentation.borderColor

  // MergeBox owns the stack rebase trigger so the mutation observer (and its
  // polling + error handling) lives in a component that stays mounted for the whole rebase.
  const {onRebaseStack, rebaseErrorMessage} = useRebaseStack({
    stackEntries: stackSectionStatus.stackEntries || [],
    stackMetadata: stackSectionStatus.stackMetadata,
  })
  const handleConfirmRebase = useCallback(() => {
    onRebaseStack()
    // Hand off to the `RebasingStacksSection` takeover, which shows rebase progress and
    // only lets the user leave once the rebase has fully settled.
    setIsRebasingStack(true)
  }, [onRebaseStack])

  // Leaving the takeover unmounts the focused "Done" button, which would
  // otherwise drop focus to the document body. Move focus to the primary merge
  // action — the natural next step after a rebase — using the merge section's
  // existing focus-on-mount flag, which fires once that button re-renders
  // (WCAG 2.4.3 Focus Order).
  const handleRebaseDone = useCallback(() => {
    focusPrimaryMergeButton()
    setIsRebasingStack(false)
  }, [focusPrimaryMergeButton])

  const stackSection = stackSectionStatus.shouldRender ? (
    <StackSection
      pullRequestId={pullRequestData.id}
      status={stackSectionStatus.sectionStatus}
      baseRefName={pullRequestData.stackedBaseRefName}
      loadingEntryCount={stackSectionStatus.loadingEntryCount}
      isStackMergeInProgress={isStackMergeInProgress}
      viewerCanDisableAutoMerge={
        pullRequestData.viewerCanDisableAutoMerge && !stackSectionStatus.isAsyncStackMergePending
      }
      shouldOnlyRenderStackList={stackSectionStatus.shouldOnlyRenderStackList}
      stackMetadata={stackSectionStatus.stackMetadata}
      stackEntries={stackSectionStatus.stackEntries || []}
      entriesBeforeCount={stackSectionStatus.eligibleToMergeEntriesBeforeCount}
      entriesToMergeCount={stackSectionStatus.entriesToMergeCount}
      autoMergeRangePullRequestCount={stackSectionStatus.autoMergeRangePullRequestCount}
    />
  ) : null

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div className={clsx(styles.mergePartialContainer, 'position-relative partial-pull-merging-analytics-js')}>
      {!shouldHideIcon && (
        <div className="d-none d-lg-block" data-testid="mergeability-icon-wrapper">
          <MergeabilityIcon
            icon={mergeBoxIcon}
            ariaLabel={mergeStatusPresentation.title}
            iconBackgroundColor={mergeBoxOverallStatusPresentation.iconColor}
          />
        </div>
      )}
      <div
        ref={mergeBoxContainerRef}
        tabIndex={-1}
        data-testid="mergebox-border-container"
        className={clsx(
          shouldHideMergeSection ? styles.mergeBoxAdjustBorders : '',
          !hideBorder && `border rounded-2 ${borderColor}`,
        )}
      >
        {/** If actively rebasing, only show RebasingStacksSection */}
        {isRebasingStack && (
          <RebasingStacksSection
            baseRefName={pullRequestData.stackedBaseRefName}
            stackEntries={stackSectionStatus.stackEntries || []}
            stackNumber={stackSectionStatus.stackMetadata?.number}
            rebaseFailed={rebaseErrorMessage !== null}
            onDone={handleRebaseDone}
          />
        )}

        {/** All other sections and content render if the rebasing takeover is not active */}
        {!isRebasingStack && (
          <>
            {closedOrMergedStateSectionStatus.shouldRender || !mergeRequirements ? (
              <>
                <ClosedOrMergedStateMergeBox
                  isCrossRepo={pullRequestData.isCrossRepo}
                  state={pullRequestData.state}
                  headRefName={pullRequestData.headRefName}
                  headRepository={pullRequestData.headRepository}
                  baseRepository={pullRequestData.baseRepository}
                  viewerCanDeleteHeadRef={pullRequestData.viewerCanDeleteHeadRef}
                  viewerCanRestoreHeadRef={pullRequestData.viewerCanRestoreHeadRef}
                  deprovisionableCodespaces={pullRequestData.deprovisionableCodespaces}
                />
                {stackSection}
              </>
            ) : mergeQueueSectionStatus.shouldRender ? (
              <>
                <MergeQueueSection
                  viewerCanAddAndRemoveFromMergeQueue={pullRequestData.viewerCanAddAndRemoveFromMergeQueue}
                  mergeQueueEntry={pullRequestData.mergeQueueEntry}
                  mergeQueue={pullRequestData.mergeQueue}
                  onDequeueSuccess={focusPrimaryMergeButton}
                />
                {stackSection}
              </>
            ) : (
              <>
                {shouldRenderAllSections && (
                  <>
                    {reviewerSectionStatus.shouldRender && (
                      <ReviewerSection
                        consolidatedFailureReasons={reviewerSectionStatus.consolidatedFailureReasons}
                        helpUrl={helpUrl}
                        latestOpinionatedReviews={pullRequestData.latestOpinionatedReviews}
                        numReviewsRequired={reviewerSectionStatus.numReviewsRequired}
                        pendingRequestedReviews={pullRequestData.pendingReviewRequests}
                        pullRequestId={pullRequestData.id}
                        reviewsState={reviewerSectionStatus.sectionStatus}
                        viewerCanDismissReviews={pullRequestData.viewerCanDismissReviews}
                        viewerCanReRequestReviews={pullRequestData.viewerCanReRequestReviews}
                      />
                    )}
                    {proofOfPresenceRequirement && <ProofOfPresenceSection requirement={proofOfPresenceRequirement} />}
                    <ErrorBoundary boundaryName="MergeBox_ChecksSection" fallback={<ChecksSectionFetchFailure />}>
                      <ChecksSection
                        pullRequestId={pullRequestData.id}
                        pullRequestHeadSha={pullRequestData.headRefOid}
                        focusPrimaryMergeButton={focusPrimaryMergeButton}
                        sectionStatus={checksSectionStatus.sectionStatus}
                        shouldRender={checksSectionStatus.shouldRender}
                        mergeBoxUserPreferences={mergeBoxUserPreferences}
                        viewerCanUseCopilotCodingAgent={pullRequestData.viewerCanUseCopilotCodingAgent}
                        viewerHasCEOrCBAccess={pullRequestData.viewerHasCEOrCBAccess}
                        headRefName={pullRequestData.headRefName}
                        headRepositoryOwner={pullRequestData.headRepository?.ownerLogin}
                        headRepositoryName={pullRequestData.headRepository?.name}
                      />
                    </ErrorBoundary>
                    {conflictsSectionStatus.shouldRender && conflictsSectionStatus.conflictsCondition && (
                      <ConflictsSection
                        advisoryWorkspace={pullRequestData.advisoryWorkspace}
                        baseRefName={pullRequestData.baseRefName}
                        headRefOid={pullRequestData.headRefOid}
                        conflictsCondition={conflictsSectionStatus.conflictsCondition}
                        conflictsState={conflictsSectionStatus.sectionStatus}
                        resourcePath={pullRequestData.resourcePath}
                        canUserPushToBase={canUserPushToBase}
                        viewerCanUseCopilotCodingAgent={pullRequestData.viewerCanUseCopilotCodingAgent}
                        viewerCanUpdateBranch={pullRequestData.viewerCanUpdateBranch}
                        viewerLogin={viewerLogin}
                        viewerUpdateMethods={pullRequestData.viewerUpdateMethods}
                        stackState={{
                          isStacked: stackSectionStatus.isStacked,
                        }}
                      />
                    )}
                    <StacksMergeConflictsAndRebaseSection
                      onConfirmRebase={handleConfirmRebase}
                      status={stacksMergeConflictsAndRebaseSectionStatus.sectionStatus}
                      stackBaseRefName={pullRequestData.stackedBaseRefName}
                      conflicts={
                        stacksMergeConflictsAndRebaseSectionStatus.currentStackEntryMergeConflictsFailure?.conflicts ??
                        []
                      }
                      stackEntries={stackSectionStatus.stackEntries || []}
                      stackMetadata={stackSectionStatus.stackMetadata}
                      canRebase={canRebaseStack}
                    />
                    {blockedSectionStatus.shouldRender && (
                      <BlockedSection failingConditionsAndRules={blockedSectionStatus.failingConditionsAndRules} />
                    )}
                    {draftStateSectionStatus.shouldRender && (
                      <DraftStateSection
                        viewerCanUpdate={pullRequestData.viewerCanUpdate}
                        viewerCanMarkReadyForReview={pullRequestData.viewerCanMarkReadyForReview}
                        draftInclusionEnabled={featureStates.pullRequestCreationCapIncludeDrafts === 'ENABLED'}
                        helpUrl={helpUrl}
                        viewerPrCreationLimitInfo={pullRequestData.viewerPrCreationLimitInfo}
                      />
                    )}
                    {stackSection}
                  </>
                )}
                {/* The section's own IpmErrorBoundary counts the failure as an IPM metric, but it
                    disappears when IPM telemetry is off and cannot catch a throw from the section's
                    own render, so the merge box keeps a boundary here too. */}
                {pullRequestData.mergeBoxNudge && (
                  <ErrorBoundary boundaryName="MergeBox_Nudge" fallback={null}>
                    <MergeBoxNudgeSection nudge={pullRequestData.mergeBoxNudge} />
                  </ErrorBoundary>
                )}
                {showAgentMerge && (
                  <ErrorBoundary boundaryName="MergeBox_AgentMergeSection" fallback={null}>
                    <Suspense fallback={null}>
                      <AgentMergeSection
                        key={`${pullRequestData.id}:${pullRequestData.headRefOid}`}
                        currentHeadSha={pullRequestData.headRefOid}
                        isCrossRepo={pullRequestData.isCrossRepo}
                        isStacked={stackSectionStatus.isStacked}
                        featureState={featureStates.agentMerge}
                        focusPrimaryMergeButton={focusPrimaryMergeButton}
                        pullRequestAliveChannel={pullRequestAliveChannel}
                        viewerLogin={viewerLogin}
                      />
                    </Suspense>
                  </ErrorBoundary>
                )}
                {shouldHideMergeSection ? null : (
                  <MergeSection
                    baseRefName={mergeSectionStatus.baseRefName}
                    conflictsCondition={conflictsSectionStatus.conflictsCondition}
                    helpUrl={helpUrl}
                    isStackMergeInProgress={stackSectionStatus.isStackMergeInProgress}
                    isConfirmingMergeInfo={isConfirmingMergeInfo}
                    isStacked={stackSectionStatus.isStacked}
                    stackedEntriesToMergeCount={stackSectionStatus.entriesToMergeCount}
                    stackedEntries={stackSectionStatus.stackEntries || []}
                    entriesThatCanBeAutoMerged={stackSectionStatus.entriesThatCanBeAutoMerged}
                    stackMetadata={stackSectionStatus.stackMetadata}
                    canUserPushToBase={mergeSectionStatus.canUserPushToBase}
                    mergeRequirements={mergeRequirements}
                    mergeBoxRollupStatus={mergeBoxRollupStatus}
                    mergeCurrentlyBlockedForStacks={stackSectionStatus.isMergeCurrentlyBlocked}
                    pullRequest={pullRequestData}
                    shouldFocusPrimaryMergeButton={shouldFocusPrimaryMergeButton}
                    setShouldFocusPrimaryMergeButton={setShouldFocusPrimaryMergeButton}
                    status={mergeSectionStatus.mergeabilityStatus}
                    handleConfirmingMergeInfo={setIsConfirmingMergeInfo}
                    onStackMergePendingChange={handleStackMergePendingChange}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
      {showConvertToDraft && !(isSidePanel && isConfirmingMergeInfo) && <ConvertToDraftSection />}
      {showClosePullRequest && !isConfirmingMergeInfo && <ClosePullRequestSection />}
    </div>
  )
}
